import React, { useEffect, useState, useMemo, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { getProjectStatusColorHex, normalizeProjectStatus } from "../lib/projectStatus";
import { 
  format, 
  addMonths, 
  subMonths, 
  startOfYear, 
  endOfYear, 
  eachMonthOfInterval, 
  isWithinInterval, 
  differenceInDays, 
  startOfMonth, 
  endOfMonth,
  max,
  min,
  addDays,
  isSameMonth,
  getDaysInMonth
} from "date-fns";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer, 
  Cell,
  ReferenceLine,
  Legend
} from "recharts";
import * as d3 from "d3";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, PoundSterling, Info } from "lucide-react";
import { formatCurrency, cn } from "../utils";

interface ProjectGanttData {
  id: string;
  projectName: string;
  clientName: string;
  startDate: Date | null;
  endDate: Date | null;
  totalCost: number;
  currency: string;
  status: string;
}

export function ProjectGantt() {
  const { user, profile } = useAuth();
  const [projects, setProjects] = useState<ProjectGanttData[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewDate, setViewDate] = useState(new Date());
  const ganttRef = useRef<SVGSVGElement>(null);
  const isDarkMode = typeof document !== "undefined" && document.documentElement.classList.contains("dark");

  useEffect(() => {
    if (!user || !profile) return;

    const fetchProjects = async () => {
      try {
        const res = await fetch("/api/projects", {
          headers: { "x-user-uid": user.uid }
        });
        if (res.ok) {
          const data = await res.json();
          const fetched = data.map((project: any) => ({
            id: project.id,
            projectName: project.setup?.projectName || project.projectName || "Untitled Project",
            clientName: project.clientName || "",
            startDate: (project.projectStartEst || (project.setup && project.setup.projectStartEst)) ? new Date(project.projectStartEst || project.setup.projectStartEst) : null,
            endDate: (project.projectEndEst || (project.setup && project.setup.projectEndEst)) ? new Date(project.projectEndEst || project.setup.projectEndEst) : null,
            totalCost: project.latestTotalCost || 0,
            currency: project.currency || "GBP",
            status: project.status || (project.setup && project.setup.status) || "Bidding"
          }));
          setProjects(fetched);
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [user, profile]);

  const yearStart = startOfYear(viewDate);
  const yearEnd = endOfYear(viewDate);
  const months = eachMonthOfInterval({ start: yearStart, end: yearEnd });

  const filteredProjects = useMemo(() => {
    return projects.filter(project => {
      if (!project.startDate || !project.endDate) return false;
      // Check if project overlaps with the selected year
      return project.startDate <= yearEnd && project.endDate >= yearStart;
    });
  }, [projects, yearStart, yearEnd]);

  // Income Projection Calculation
  const incomeData = useMemo(() => {
    const monthlyTotals: Record<string, { delivered: number; production: number; bidding: number; total: number }> = {};
    
    // Initialize months
    months.forEach(m => {
      monthlyTotals[format(m, "yyyy-MM")] = { delivered: 0, production: 0, bidding: 0, total: 0 };
    });

    filteredProjects.forEach(project => {
      const normalizedStatus = normalizeProjectStatus(project.status);
      if (normalizedStatus === "Hold" || normalizedStatus === "Lost" || normalizedStatus === "Archived") return;
      if (!project.startDate || !project.endDate || project.totalCost === 0) return;

      const durationDays = differenceInDays(project.endDate, project.startDate) + 1;
      if (durationDays <= 0) return;

      const dailyRate = project.totalCost / durationDays;

      // For each month in the view, calculate overlap
      months.forEach(month => {
        const mStart = startOfMonth(month);
        const mEnd = endOfMonth(month);

        // Find overlap between [projectStart, projectEnd] and [mStart, mEnd]
        const overlapStart = max([project.startDate!, mStart]);
        const overlapEnd = min([project.endDate!, mEnd]);

        if (overlapStart <= overlapEnd) {
          const overlapDays = differenceInDays(overlapEnd, overlapStart) + 1;
          const monthKey = format(month, "yyyy-MM");
          const amount = dailyRate * overlapDays;
          
          if (normalizedStatus === "Delivered") {
            monthlyTotals[monthKey].delivered += amount;
          } else if (normalizedStatus === "Active") {
            monthlyTotals[monthKey].production += amount;
          } else if (normalizedStatus === "Bidding" || normalizedStatus === "Pipeline") {
            monthlyTotals[monthKey].bidding += amount;
          }
          monthlyTotals[monthKey].total += amount;
        }
      });
    });

    return months.map(m => {
      const key = format(m, "yyyy-MM");
      const totals = monthlyTotals[key];
      return {
        month: format(m, "MMM"),
        fullName: format(m, "MMMM yyyy"),
        delivered: Math.round(totals.delivered),
        production: Math.round(totals.production),
        bidding: Math.round(totals.bidding),
        income: Math.round(totals.total),
        key
      };
    });
  }, [filteredProjects, months]);

  // Gantt Chart Rendering with D3
  useEffect(() => {
    if (!ganttRef.current || filteredProjects.length === 0) {
      if (ganttRef.current) {
        d3.select(ganttRef.current).selectAll("*").remove();
      }
      return;
    }

    const renderGantt = () => {
      if (!ganttRef.current) return;
      const svg = d3.select(ganttRef.current);
      svg.selectAll("*").remove();
      d3.selectAll(".gantt-tooltip").remove();

      const margin = { top: 40, right: 20, bottom: 20, left: 200 };
      const width = ganttRef.current.parentElement?.clientWidth ? ganttRef.current.parentElement.clientWidth - margin.left - margin.right : 800;
      const rowHeight = 40;
      const height = (filteredProjects.length * rowHeight) + margin.top + margin.bottom;

      svg.attr("width", "100%").attr("height", height);

      const x = d3.scaleTime()
        .domain([yearStart, yearEnd])
        .range([0, width]);

      const y = d3.scaleBand()
        .domain(filteredProjects.map(p => p.id))
        .range([margin.top, height - margin.bottom])
        .padding(0.2);

      const g = svg.append("g")
        .attr("transform", `translate(${margin.left},0)`);

      // Grid lines for months
      g.selectAll(".grid-line")
        .data(months)
        .enter()
        .append("line")
        .attr("class", "grid-line")
        .attr("x1", d => x(d))
        .attr("x2", d => x(d))
        .attr("y1", margin.top)
        .attr("y2", height - margin.bottom)
        .attr("stroke", isDarkMode ? "#3f3a32" : "#e2e8f0")
        .attr("stroke-dasharray", "4,4");

      // Month labels
      g.selectAll(".month-label")
        .data(months)
        .enter()
        .append("text")
        .attr("class", "month-label")
        .attr("x", d => x(d) + 5)
        .attr("y", margin.top - 10)
        .text(d => format(d, "MMM"))
        .attr("font-size", "10px")
        .attr("fill", isDarkMode ? "#8f816d" : "#94a3b8")
        .attr("font-weight", "600");

      // Today indicator
      const today = new Date();
      if (isWithinInterval(today, { start: yearStart, end: yearEnd })) {
        g.append("line")
          .attr("x1", x(today))
          .attr("x2", x(today))
          .attr("y1", margin.top - 20)
          .attr("y2", height - margin.bottom)
          .attr("stroke", "#ef4444")
          .attr("stroke-width", 2);

        g.append("text")
          .attr("x", x(today))
          .attr("y", margin.top - 25)
          .attr("text-anchor", "middle")
          .text("Today")
          .attr("font-size", "10px")
          .attr("fill", "#ef4444")
          .attr("font-weight", "bold");
      }

      // Tooltip
      const tooltip = d3.select("body").append("div")
        .attr("class", "gantt-tooltip")
        .style("position", "absolute")
        .style("visibility", "hidden")
        .style("background-color", isDarkMode ? "#292621" : "white")
        .style("border", `1px solid ${isDarkMode ? "#39342c" : "#e2e8f0"}`)
        .style("padding", "8px")
        .style("border-radius", "6px")
        .style("box-shadow", "0 4px 6px -1px rgb(0 0 0 / 0.1)")
        .style("font-size", "12px")
        .style("color", isDarkMode ? "#f3f1ee" : "#0f172a")
        .style("z-index", "1000")
        .style("pointer-events", "none");

      const dragMove = d3.drag<SVGGElement, ProjectGanttData>()
        .on("start", function() {
          d3.select(this).select("rect.project-bar-rect").attr("opacity", 1);
        })
        .on("drag", function(event) {
          const gNode = d3.select(this);
          const rect = gNode.select("rect.project-bar-rect");
          const leftHandle = gNode.select(".drag-handle-left");
          const rightHandle = gNode.select(".drag-handle-right");
          
          const currentX = parseFloat(rect.attr("x"));
          const newX = currentX + event.dx;
          
          rect.attr("x", newX);
          leftHandle.attr("x", newX - 4);
          rightHandle.attr("x", newX + parseFloat(rect.attr("width")) - 4);
        })
        .on("end", async function(event, d) {
          d3.select(this).select("rect.project-bar-rect").attr("opacity", 0.8);
          
          const rect = d3.select(this).select("rect.project-bar-rect");
          const newX = parseFloat(rect.attr("x"));
          
          // Original X
          const originalX = x(max([d.startDate!, yearStart]));
          
          if (newX === originalX) return; // no movement
          
          // Calculate time delta
          const timeDelta = x.invert(newX).getTime() - x.invert(originalX).getTime();
          
          const newStartDate = new Date(d.startDate!.getTime() + timeDelta);
          const newEndDate = new Date(d.endDate!.getTime() + timeDelta);

          try {
            await fetch(`/api/projects/${d.id}`, {
              method: "PUT",
              headers: { 
                "Content-Type": "application/json",
                "x-user-uid": user.uid
              },
              body: JSON.stringify({
                projectStartEst: newStartDate.toISOString().split('T')[0],
                projectEndEst: newEndDate.toISOString().split('T')[0]
              })
            });
            // Update local state
            setProjects(prev => prev.map(p => p.id === d.id ? {
              ...p,
              startDate: newStartDate,
              endDate: newEndDate
            } : p));
          } catch (error) {
            console.error("Error updating project dates:", error);
          }
        });

      const dragLeft = d3.drag<SVGRectElement, ProjectGanttData>()
        .on("start", function(event) {
          event.sourceEvent.stopPropagation();
          d3.select(this.parentNode as SVGGElement).select("rect.project-bar-rect").attr("opacity", 1);
        })
        .on("drag", function(event) {
          const rect = d3.select(this.parentNode as SVGGElement).select("rect.project-bar-rect");
          const leftHandle = d3.select(this);
          
          const currentX = parseFloat(rect.attr("x"));
          const currentWidth = parseFloat(rect.attr("width"));
          
          const newX = currentX + event.dx;
          const newWidth = currentWidth - event.dx;
          
          if (newWidth > 0) {
            rect.attr("x", newX).attr("width", newWidth);
            leftHandle.attr("x", newX - 4);
          }
        })
        .on("end", async function(event, d) {
          d3.select(this.parentNode as SVGGElement).select("rect.project-bar-rect").attr("opacity", 0.8);
          
          const rect = d3.select(this.parentNode as SVGGElement).select("rect.project-bar-rect");
          const newX = parseFloat(rect.attr("x"));
          
          // Calculate new start date
          const newStartDate = x.invert(newX);

          try {
            await fetch(`/api/projects/${d.id}`, {
              method: "PUT",
              headers: { 
                "Content-Type": "application/json",
                "x-user-uid": user.uid
              },
              body: JSON.stringify({
                projectStartEst: newStartDate.toISOString().split('T')[0]
              })
            });
            // Update local state
            setProjects(prev => prev.map(p => p.id === d.id ? {
              ...p,
              startDate: newStartDate
            } : p));
          } catch (error) {
            console.error("Error updating project start date:", error);
          }
        });

      const dragRight = d3.drag<SVGRectElement, ProjectGanttData>()
        .on("start", function(event) {
          event.sourceEvent.stopPropagation();
          d3.select(this.parentNode as SVGGElement).select("rect.project-bar-rect").attr("opacity", 1);
        })
        .on("drag", function(event) {
          const rect = d3.select(this.parentNode as SVGGElement).select("rect.project-bar-rect");
          const rightHandle = d3.select(this);
          
          const currentWidth = parseFloat(rect.attr("width"));
          const newWidth = currentWidth + event.dx;
          
          if (newWidth > 0) {
            rect.attr("width", newWidth);
            rightHandle.attr("x", parseFloat(rect.attr("x")) + newWidth - 4);
          }
        })
        .on("end", async function(event, d) {
          d3.select(this.parentNode as SVGGElement).select("rect.project-bar-rect").attr("opacity", 0.8);
          
          const rect = d3.select(this.parentNode as SVGGElement).select("rect.project-bar-rect");
          const newX = parseFloat(rect.attr("x"));
          const newWidth = parseFloat(rect.attr("width"));
          
          // Calculate new end date
          const newEndDate = x.invert(newX + newWidth);

          try {
            await fetch(`/api/projects/${d.id}`, {
              method: "PUT",
              headers: { 
                "Content-Type": "application/json",
                "x-user-uid": user.uid
              },
              body: JSON.stringify({
                projectEndEst: newEndDate.toISOString().split('T')[0]
              })
            });
            // Update local state
            setProjects(prev => prev.map(p => p.id === d.id ? {
              ...p,
              endDate: newEndDate
            } : p));
          } catch (error) {
            console.error("Error updating project end date:", error);
          }
        });

      // Project Bars
      const bars = g.selectAll(".project-bar")
        .data(filteredProjects.filter(p => p.startDate && p.endDate))
        .enter()
        .append("g")
        .attr("class", "project-bar")
        .style("cursor", "move")
        .call(dragMove as any);

      bars.append("rect")
        .attr("class", "project-bar-rect")
        .attr("x", d => x(max([d.startDate!, yearStart])))
        .attr("y", d => y(d.id)!)
        .attr("width", d => {
          const start = max([d.startDate!, yearStart]);
          const end = min([d.endDate!, yearEnd]);
          return Math.max(0, x(end) - x(start));
        })
        .attr("height", y.bandwidth())
        .attr("rx", 4)
        .attr("fill", d => getProjectStatusColorHex(d.status))
        .attr("opacity", 0.8)
        .on("mouseover", function(event, d) { 
          d3.select(this).attr("opacity", 1); 
          tooltip.style("visibility", "visible")
                 .html(`
                   <div style="font-weight:700;color:${isDarkMode ? "#f3f1ee" : "#0f172a"}">${d.projectName}</div>
                   <div style="color:${isDarkMode ? "#7fa2c8" : "#4d6b88"};font-weight:600;margin:4px 0">Total Cost: ${formatCurrency(d.totalCost, d.currency)}</div>
                   <div style="font-size:12px;color:${isDarkMode ? "#b0a390" : "#64748b"}">${format(d.startDate!, "MMM d, yyyy")} - ${format(d.endDate!, "MMM d, yyyy")}</div>
                 `);
        })
        .on("mousemove", function(event) {
          tooltip.style("top", (event.pageY - 10) + "px")
                 .style("left", (event.pageX + 10) + "px");
        })
        .on("mouseout", function() { 
          d3.select(this).attr("opacity", 0.8); 
          tooltip.style("visibility", "hidden");
        });

      // Left Handle
      bars.append("rect")
        .attr("class", "drag-handle-left")
        .attr("x", d => x(max([d.startDate!, yearStart])) - 4)
        .attr("y", d => y(d.id)!)
        .attr("width", 8)
        .attr("height", y.bandwidth())
        .attr("fill", "transparent")
        .style("cursor", "ew-resize")
        .call(dragLeft as any);

      // Right Handle
      bars.append("rect")
        .attr("class", "drag-handle-right")
        .attr("x", d => {
          const start = max([d.startDate!, yearStart]);
          const end = min([d.endDate!, yearEnd]);
          return x(start) + Math.max(0, x(end) - x(start)) - 4;
        })
        .attr("y", d => y(d.id)!)
        .attr("width", 8)
        .attr("height", y.bandwidth())
        .attr("fill", "transparent")
        .style("cursor", "ew-resize")
        .call(dragRight as any);

      // Project Labels (Y-axis)
      svg.append("g")
        .selectAll(".project-label")
        .data(filteredProjects)
        .enter()
        .append("text")
        .attr("class", "project-label")
        .attr("x", margin.left - 10)
        .attr("y", d => y(d.id)! + y.bandwidth() / 2)
        .attr("text-anchor", "end")
        .attr("alignment-baseline", "middle")
        .text(d => d.projectName)
        .attr("font-size", "12px")
        .attr("font-weight", "600")
        .attr("fill", isDarkMode ? "#f3f1ee" : "#1e293b");

      svg.append("g")
        .selectAll(".client-label")
        .data(filteredProjects)
        .enter()
        .append("text")
        .attr("class", "client-label")
        .attr("x", margin.left - 10)
        .attr("y", d => y(d.id)! + y.bandwidth() / 2 + 14)
        .attr("text-anchor", "end")
        .attr("alignment-baseline", "middle")
        .text(d => d.clientName)
        .attr("font-size", "10px")
        .attr("fill", isDarkMode ? "#b0a390" : "#64748b");
    };

    renderGantt();
    window.addEventListener("resize", renderGantt);
    return () => {
      window.removeEventListener("resize", renderGantt);
      d3.selectAll(".gantt-tooltip").remove();
    };
  }, [filteredProjects, viewDate, yearStart, yearEnd, months, isDarkMode]);

  const handlePrevYear = () => setViewDate(prev => addMonths(prev, -12));
  const handleNextYear = () => setViewDate(prev => addMonths(prev, 12));
  const handleReset = () => setViewDate(new Date());

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col pb-12">
      <div className="bg-[#fff0e5] dark:bg-[#311d13] px-6 py-4 rounded-lg border border-[#f3caa8] dark:border-[#6c3c1b] flex items-center justify-between mb-6 shadow-sm shrink-0">
        <div className="flex flex-col">
          <h2 className="text-2xl font-brandon font-medium text-slate-900 dark:text-slate-100">Work Calendar</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Visualize project timelines, milestones, and resource allocation</p>
        </div>
        <div className="flex items-center gap-2 bg-white dark:bg-slate-800 p-1 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">
          <button 
            onClick={handlePrevYear}
            className="p-1.5 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-md text-slate-600 dark:text-slate-300 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="px-4 py-1 text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <CalendarIcon className="w-4 h-4 text-indigo-500 dark:text-indigo-400" />
            {format(viewDate, "yyyy")}
          </div>
          <button 
            onClick={handleNextYear}
            className="p-1.5 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-md text-slate-600 dark:text-slate-300 transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="w-px h-4 bg-slate-200 dark:bg-slate-700 mx-1"></div>
          <button 
            onClick={handleReset}
            className="px-3 py-1 text-xs font-medium text-indigo-600 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-md transition-colors"
          >
            Current Year
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-8">
        {/* Gantt Chart Section */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="p-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/70 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-indigo-500 dark:bg-indigo-400 rounded-sm"></div>
            <span className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Projects Timeline</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-px h-3 bg-red-500"></div>
              <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Current Date</span>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <svg ref={ganttRef} className="w-full min-w-[800px]"></svg>
        </div>
        {filteredProjects.length === 0 && (
          <div className="p-12 text-center text-slate-400 dark:text-slate-500 italic">
            No projects found for the selected timeframe.
          </div>
        )}
      </div>

      {/* Income Projection Section */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-4">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-50 dark:bg-emerald-950/30 rounded-xl flex items-center justify-center text-emerald-600 dark:text-emerald-300 border border-emerald-100 dark:border-emerald-900/40">
              <PoundSterling className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Monthly Income Projection</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">Estimated revenue based on project duration and total bid</p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 dark:bg-slate-950 rounded-lg border border-slate-100 dark:border-slate-800">
            <Info className="w-4 h-4 text-slate-400 dark:text-slate-500" />
            <span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium leading-tight max-w-[200px]">
              Revenue is calculated by distributing the total project cost evenly across its duration.
            </span>
          </div>
        </div>

        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={incomeData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDarkMode ? "#39342c" : "#f1f5f9"} />
              <XAxis 
                dataKey="month" 
                axisLine={false} 
                tickLine={false} 
                tick={{ fill: isDarkMode ? '#8f816d' : '#94a3b8', fontSize: 12, fontWeight: 500 }}
                dy={10}
              />
              <YAxis 
                axisLine={false} 
                tickLine={false} 
                tick={{ fill: isDarkMode ? '#8f816d' : '#94a3b8', fontSize: 12 }}
                tickFormatter={(value) => `£${value.toLocaleString()}`}
              />
              <RechartsTooltip 
                cursor={{ fill: isDarkMode ? '#1d1b18' : '#f8fafc' }}
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div className="bg-white dark:bg-slate-900 p-3 border border-slate-200 dark:border-slate-800 shadow-xl rounded-lg min-w-[200px]">
                        <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2">{payload[0].payload.fullName}</p>
                        <div className="space-y-1.5">
                          <div className="flex justify-between items-center gap-4">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-[#e4a4a5]"></div>
                              <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase">Bidding</span>
                            </div>
                            <span className="text-xs font-bold text-slate-700 dark:text-slate-200">{formatCurrency(payload[0].payload.bidding, "GBP")}</span>
                          </div>
                          <div className="flex justify-between items-center gap-4">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-[#234d6f]"></div>
                              <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase">Active</span>
                            </div>
                            <span className="text-xs font-bold text-slate-700 dark:text-slate-200">{formatCurrency(payload[0].payload.production, "GBP")}</span>
                          </div>
                          <div className="flex justify-between items-center gap-4">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-[#597058]"></div>
                              <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase">Delivered</span>
                            </div>
                            <span className="text-xs font-bold text-slate-700 dark:text-slate-200">{formatCurrency(payload[0].payload.delivered, "GBP")}</span>
                          </div>
                          <div className="pt-1.5 mt-1.5 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center gap-4">
                            <span className="text-[10px] font-black text-slate-900 dark:text-slate-100 uppercase">Total</span>
                            <span className="text-sm font-black text-indigo-600 dark:text-indigo-300">{formatCurrency(payload[0].payload.income, "GBP")}</span>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Bar dataKey="delivered" name="Delivered" stackId="a" fill="#597058" radius={[3, 3, 3, 3]} barSize={40} />
              <Bar dataKey="production" name="Active" stackId="a" fill="#234d6f" radius={[3, 3, 3, 3]} barSize={40} />
              <Bar dataKey="bidding" name="Bidding" stackId="a" fill="#e4a4a5" radius={[3, 3, 3, 3]} barSize={40} />
              <Legend 
                verticalAlign="top" 
                align="right" 
                iconType="circle"
                wrapperStyle={{ paddingBottom: '20px', fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase' }}
              />
              {isWithinInterval(new Date(), { start: yearStart, end: yearEnd }) && (
                <ReferenceLine 
                  x={format(new Date(), "MMM")} 
                  stroke="#ef4444" 
                  strokeDasharray="3 3"
                  label={{ 
                    position: 'top', 
                    value: 'Current Month', 
                    fill: '#ef4444', 
                    fontSize: 10, 
                    fontWeight: 'bold' 
                  }} 
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-12 gap-3">
          {incomeData.map((data) => (
            <div key={data.key} className="p-2.5 rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/70 min-w-0">
              <div className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">{data.month}</div>
              <div className="text-[13px] font-bold text-slate-900 dark:text-slate-100 truncate">
                {formatCurrency(data.income, "GBP")}
              </div>
            </div>
          ))}
        </div>
      </div>
      </div>
    </div>
  );
}
